/**
 * Session-based seed utilities for deterministic randomization.
 * Same session ID produces the same "random" order so tiles don't reorder
 * when the user stays on the page (e.g. opening a video). Order only changes
 * on new session (e.g. new tab or after leaving the URL and returning with a fresh load).
 */

const SESSION_STORAGE_KEY = 'app_session_id';

/**
 * Returns a session ID that persists for the lifetime of the tab (sessionStorage).
 * New tab or page refresh gets a new ID, giving a new random order per visit.
 */
export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') {
    return `ssr-${Date.now()}`;
  }
  let id = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!id) {
    id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(SESSION_STORAGE_KEY, id);
  }
  return id;
}

/**
 * Returns a session seed string suitable for sending to APIs (e.g. seed query param).
 * Stable for the session so backend can return deterministic "random" order.
 */
export function getSessionSeed(): string {
  return getOrCreateSessionId();
}

/**
 * Simple string hash to derive a numeric seed (djb2).
 */
function hashString(str: string): number {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = (h * 33) ^ str.charCodeAt(i);
  }
  return h >>> 0;
}

/**
 * Seeded PRNG (mulberry32). Returns a number in [0, 1).
 * Same seed + same call order => same sequence.
 */
function seededRandomGenerator(seed: number): () => number {
  return function next() {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Returns a deterministic "random" number in [0, 1) for the given seed and optional key.
 * Same seed + key => same value. Use different keys for different random choices in the same view.
 */
export function seededRandom(seed: string, key: string = 'default'): number {
  const n = hashString(seed + key);
  const gen = seededRandomGenerator(n);
  return gen();
}

/**
 * Shuffles a copy of the array using a seed so the order is stable for the session.
 * Same seed + same array length/content identity => same order.
 */
export function shuffleWithSeed<T>(array: T[], seed: string, key: string = 'shuffle'): T[] {
  const arr = [...array];
  if (arr.length <= 1) return arr;
  const h = hashString(seed + key);
  const random = seededRandomGenerator(h);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Picks a deterministic random index in [0, length) for the given seed and key.
 */
export function seededRandomIndex(length: number, seed: string, key: string): number {
  if (length <= 0) return 0;
  return Math.floor(seededRandom(seed, key) * length);
}
